import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { type PlaidLinkRecord, plaidLinksQueryOptions } from "@/lib/data";
import { usePlaidLinkSession } from "@/lib/use-plaid-link-session";

/**
 * Asks the applicant to log in to a bank again when Plaid reports its
 * connection broken or about to expire, and repairs it in place with Link's
 * update mode. Once repaired the link reports healthy and the banner goes.
 */
export function PlaidReconnectBanner() {
  const { data } = useQuery(plaidLinksQueryOptions);
  const link = data?.plaid_links.find((l) => l.needs_reconnect);

  return link ? <ReconnectNotice key={link.id} link={link} /> : null;
}

const noop = async () => {};

function ReconnectNotice({ link }: { link: PlaidLinkRecord }) {
  const { start, starting, saving, error } = usePlaidLinkSession({
    reconnectLinkId: link.id,
    onLinked: noop,
  });
  const bank = link.institution_name ?? "Your bank";

  return (
    <div
      role="status"
      className="mb-6 flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning-muted p-4"
    >
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" />
      <div className="min-w-0 grow">
        <p className="text-sm font-medium text-foreground">
          {bank} needs to be reconnected
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Log in to your bank again so we can keep verifying your income.
        </p>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </div>
      <Button
        size="sm"
        className="shrink-0"
        onClick={start}
        disabled={starting || saving}
      >
        {starting || saving ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : null}
        {saving ? "Reconnecting…" : "Reconnect"}
      </Button>
    </div>
  );
}
